import { NetworkStatus } from "@apollo/client";
import { useCallback, useMemo, useState } from "react";

import { PollingIntervalGroups } from "apollo/constants";
import usePolledQuery from "apollo/usePolledQuery";
import { SearchQueryOrder, SearchQueryRequestedPage } from "types/generated";

import {
  SpaceDetailsAccessGrantSource,
  SpaceDetailsAccessGrantedVia,
} from "../components/AccessCells/types";
import { API_KEYS_WITH_ACCESS_TO_SPACE, API_KEYS_WITH_ACCESS_TO_SPACE_COUNT } from "./gql";
import { SpaceDetailsApiKey } from "./types";

type UseSpaceDetailsApiKeysProps = {
  spaceId: string;
  orderBy: SearchQueryOrder;
  first: number;
  fullTextSearch: string | null;
};

const useSpaceDetailsApiKeys = ({
  spaceId,
  orderBy,
  first,
  fullTextSearch,
}: UseSpaceDetailsApiKeysProps) => {
  const [requestedPage, setRequestedPage] = useState<SearchQueryRequestedPage | null>(null);
  const [after, setAfter] = useState<string | null>(null);

  const resetKey = `${spaceId}:${orderBy.field}:${orderBy.direction}:${fullTextSearch ?? ""}`;
  const [previousResetKey, setPreviousResetKey] = useState(resetKey);

  if (previousResetKey !== resetKey) {
    setPreviousResetKey(resetKey);
    setRequestedPage(null);
    setAfter(null);
  }

  const variables = useMemo(
    () => ({
      space: spaceId,
      input: {
        predicates: [],
        orderBy: [orderBy],
        fullTextSearch,
        first,
        after,
        requestedPage,
      },
    }),
    [spaceId, orderBy, first, after, requestedPage, fullTextSearch]
  );

  const { error, loading, data, networkStatus, previousData } = usePolledQuery(
    API_KEYS_WITH_ACCESS_TO_SPACE,
    {
      variables,
      pollingGroup: PollingIntervalGroups.Lists,
      nextFetchPolicy: "cache-first",
    }
  );

  const { data: countData, previousData: previousCountData } = usePolledQuery(
    API_KEYS_WITH_ACCESS_TO_SPACE_COUNT,
    {
      variables: { space: spaceId, input: { fields: [], fullTextSearch, predicates: [] } },
      pollingGroup: PollingIntervalGroups.Lists,
      nextFetchPolicy: "cache-first",
    }
  );

  const loadApiKeys = useCallback(
    ({
      cursor,
      requestedPage,
    }: {
      cursor: string | null;
      requestedPage: SearchQueryRequestedPage | null;
    }) => {
      setAfter(cursor);
      setRequestedPage(requestedPage);
    },
    []
  );

  const initialLoading =
    loading && networkStatus === NetworkStatus.loading && !data?.apiKeysWithAccessToSpace;

  const loadingContent =
    loading &&
    (networkStatus === NetworkStatus.loading ||
      networkStatus === NetworkStatus.fetchMore ||
      networkStatus === NetworkStatus.setVariables);

  const apiKeys = useMemo<SpaceDetailsApiKey[]>(() => {
    const edges =
      data?.apiKeysWithAccessToSpace?.edges || previousData?.apiKeysWithAccessToSpace?.edges || [];

    return edges.map(({ node }) => ({
      id: node.apiKey.id,
      name: node.apiKey.name,
      roleBindings: node.roles.map((grant) => {
        const grantedVia: SpaceDetailsAccessGrantedVia = {
          kind: "space",
          id: grant.space.id,
          name: grant.space.name,
          source: grant.propagated
            ? SpaceDetailsAccessGrantSource.PROPAGATED
            : SpaceDetailsAccessGrantSource.RESIDENT,
        };

        return {
          id: `${grant.role.id}:${grantedVia.kind}:${grantedVia.id}`,
          role: { id: grant.role.id, name: grant.role.name, isSystem: grant.role.isSystem },
          grantedVia,
        };
      }),
    }));
  }, [data?.apiKeysWithAccessToSpace?.edges, previousData?.apiKeysWithAccessToSpace?.edges]);

  const totalCount =
    countData?.apiKeysWithAccessToSpaceSuggestions.filteredCount ??
    previousCountData?.apiKeysWithAccessToSpaceSuggestions.filteredCount ??
    0;

  return {
    loadApiKeys,
    error,
    apiKeys,
    initialLoading,
    loadingContent,
    totalCount,
    endCursor: data?.apiKeysWithAccessToSpace?.pageInfo.endCursor,
    startCursor: data?.apiKeysWithAccessToSpace?.pageInfo.startCursor,
    paginationResetKey: resetKey,
  };
};

export default useSpaceDetailsApiKeys;
